import * as leaflet from "leaflet";
import { TileCoordinates } from "../../lib/runescape/coordinates";
import { GameMap, levelIcon } from "../../lib/gamemap/GameMap";
import { Constants } from "../constants";
import { ActiveOpacityGroup } from "../../lib/gamemap/layers/OpacityLayer";
import { GameLayer } from "../../lib/gamemap/GameLayer";
import { Observable, observe } from "../../lib/reactive";
import { MapEntity } from "../../lib/gamemap/MapEntity";
import { Rectangle } from "../../lib/math";
import Properties from "../ui/widgets/Properties";
var observe_combined = Observable.observe_combined;
import { ScanSolving } from "./subbehaviours/scans/ScanSolving";
import { LeafletUtils } from "../../lib/gamemap/LeafletUtils";
import { LeafletPolygonConstructors } from "../../lib/gamemap/LeafletPolygonConstructors";
var areaPolygon = LeafletPolygonConstructors.areaPolygon;
var boxPolygon = LeafletPolygonConstructors.boxPolygon;
export class ScanRegionPolygon extends ActiveOpacityGroup {
    constructor(_spot) {
        super(1, 0.2);
        this._spot = _spot;
        this.update();
    }
    spot() {
        return this._spot;
    }
    setRegion(spot) {
        this._spot = spot;
        this.update();
    }
    update() {
        if (this.polygon) {
            this.polygon.remove();
            this.polygon = null;
            this.label.remove();
            this.label = null;
        }
        if (this._spot?.area) {
            this.polygon = areaPolygon(this._spot.area);
            this.label = leaflet.tooltip({
                interactive: false,
                permanent: true,
                className: "area-name",
                offset: [0, 0],
                direction: "center",
                pane: GameMap.objectPane,
                content: this._spot.name
            });
            this.polygon
                .setStyle({
                color: Constants.colors.scan_area,
                fillColor: Constants.colors.scan_area,
                interactive: false,
                pane: GameMap.areaPane
            })
                .bindTooltip(this.label)
                .addTo(this);
        }
        this.setActive(this.isActive());
    }
}
export class ScanRadiusMarker extends MapEntity {
    constructor(spot, range, include_marker, is_complement, range_is_with_meerkats) {
        super();
        this.spot = spot;
        this.range = range;
        this.include_marker = include_marker;
        this.is_complement = is_complement;
        this.range_is_with_meerkats = range_is_with_meerkats;
        this.setInteractive();
        this.setTooltip(() => {
            const props = new Properties();
            props.header(`Manual Radius Marker`);
            if (this.is_complement) {
                props.named("Position", TileCoordinates.toString(this.spot) + " (In complement area)");
                props.named("Radius", `${this.range + 15} (${this.range} base + 15 due to being in complement area)`);
            }
            else {
                props.named("Position", TileCoordinates.toString(this.spot));
                if (this.range_is_with_meerkats) {
                    props.named("Radius", `${this.range} (${this.range - 5} base + 5 from meerkats)`);
                }
                else {
                    props.named("Radius", this.range.toString());
                }
            }
            props.row(c().text("Click to remove").css("font-style", "italic"));
            return props;
        });
    }
    bounds() {
        return Rectangle.centeredOn(this.spot, this.range * 2);
    }
    async render_implementation(props) {
        if (this.include_marker) {
            leaflet.marker(LeafletUtils.latLongFromVector2(this.spot), {
                icon: levelIcon(this.spot.level, props.highlight ? 1.5 : 1),
                opacity: props.opacity,
                interactive: true,
                bubblingMouseEvents: true,
            }).addTo(this);
        }
        if (this.is_complement) {
            boxPolygon({
                topleft: { x: this.spot.x - (this.range + 15), y: this.spot.y + (this.range + 15) },
                botright: { x: this.spot.x + (this.range + 15), y: this.spot.y - (this.range + 15) }
            }).setStyle({
                interactive: false,
                className: "ctr-step-graphics"
            }).setStyle({ color: ScanSolving.PulseColors.different_level.css_string, fillOpacity: 0, }).addTo(this);
        }
        else {
            boxPolygon({
                topleft: { x: this.spot.x - this.range, y: this.spot.y + this.range },
                botright: { x: this.spot.x + this.range, y: this.spot.y - this.range }
            }).setStyle({
                interactive: false,
                className: "ctr-step-graphics"
            }).setStyle({ color: ScanSolving.PulseColors.triple.css_string, fillOpacity: 0, dashArray: [9, 6, 9, 6, 9, 18] }).addTo(this);
            boxPolygon({
                topleft: { x: this.spot.x - 2 * this.range, y: this.spot.y + 2 * this.range },
                botright: { x: this.spot.x + 2 * this.range, y: this.spot.y - 2 * this.range }
            }).setStyle({
                interactive: false,
                className: "ctr-step-graphics"
            }).setStyle({ color: ScanSolving.PulseColors.double.css_string, fillOpacity: 0, dashArray: [9, 9, 9, 18] }).addTo(this);
        }
        return null;
    }
    async contextMenu(event) {
        return {
            type: "submenu",
            text: "",
            children: []
        };
    }
}
export class AdaptiveScanRadiusMarker extends GameLayer {
    constructor() {
        super();
        this.marker_spot = observe(null);
        this.scan_range = observe({ radius: 10, meerkat: false });
        this.surface_is_complement = observe(false);
        this.fixedMarker = observe(null);
        this.manualMarker = observe(null);
        this.cursorMarker = observe(null);
        this.canBeManuallySet = observe(false);
        this.followCursor = observe(false);
        this.canBeManuallySet.subscribe(() => {
            this.manualMarker.set(null);
        });
        observe_combined({
            fixed: this.fixedMarker,
            manual: this.manualMarker,
            cursor: this.cursorMarker,
            manualEnabled: this.canBeManuallySet,
            followEnabled: this.followCursor
        }).subscribe(({ fixed, manual, cursor, manualEnabled, followEnabled }) => {
            if (followEnabled && cursor) {
                this.marker_spot.set({ coordinates: cursor, with_marker: false });
            }
            else if (manualEnabled && manual) {
                this.marker_spot.set({ coordinates: manual, with_marker: true });
            }
            else if (fixed) {
                this.marker_spot.set({ coordinates: fixed, with_marker: false });
            }
            else {
                this.marker_spot.set(null);
            }
        });
        observe_combined({ range: this.scan_range, spot: this.marker_spot }).subscribe(({ range, spot }) => {
            if (this.custom_marker) {
                this.custom_marker.remove();
                this.custom_marker = null;
            }
            if (spot) {
                let is_complement = (spot.coordinates.y < 6400) == this.surface_is_complement.value();
                this.custom_marker = new ScanRadiusMarker(spot.coordinates, range.radius, spot.with_marker, is_complement, range.meerkat).addTo(this);
            }
        });
    }
    setComplementByExampleSpot(spot) {
        this.surface_is_complement.set(spot.y >= 6400);
        return this;
    }
    setFixedSpot(spot) {
        this.fixedMarker.set(spot);
        return this;
    }
    setFollowCursor(v) {
        this.followCursor.set(v);
        return this;
    }
    setClickable(v) {
        this.canBeManuallySet.set(v);
        return this;
    }
    setRadius(radius, is_with_meerkat) {
        this.scan_range.set({ radius: radius, meerkat: is_with_meerkat });
        return this;
    }
    clearManualMarker() {
        this.manualMarker.set(null);
    }
    eventClick(event) {
        event.onPost(() => {
            if (this.canBeManuallySet.value()) {
                if ((this.custom_marker && event.active_entity == this.custom_marker) || TileCoordinates.eq2(event.tile(), this.manualMarker.value())) {
                    this.clearManualMarker();
                }
                else {
                    this.manualMarker.set(event.tile());
                }
            }
        });
    }
    eventHover(event) {
        event.onPost(() => {
            if (this.followCursor.value()) {
                this.cursorMarker.set(event.tile());
            }
        });
    }
    eventContextMenu(event) {
        event.onPre(() => {
            if (event.active_entity instanceof ScanRadiusMarker && event.active_entity == this.custom_marker) {
                event.addForEntity({
                    type: "basic",
                    text: "Remove",
                    handler: () => {
                        this.manualMarker.set(null);
                    }
                });
            }
            else if (this.canBeManuallySet.value() && !event.active_entity) {
                if (TileCoordinates.eq2(this.manualMarker.value(), event.tile())) {
                    event.add({
                        type: "basic",
                        text: "Remove Marker",
                        handler: () => {
                            this.manualMarker.set(null);
                        }
                    });
                }
                else {
                    event.add({
                        type: "basic",
                        text: "Set Marker",
                        handler: () => {
                            this.manualMarker.set(event.tile());
                        }
                    });
                }
            }
        });
    }
}
